import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft, Award, BookOpen, CheckCircle, Clock, ChevronRight, 
  Shield, Star, Utensils, Megaphone, HardHat, ClipboardList,
  Play, Lock, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// Icon mapping
const categoryIcons = {
  'utensils': Utensils,
  'megaphone': Megaphone,
  'hard-hat': HardHat,
  'clipboard': ClipboardList
};

const TrainingHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hubData, setHubData] = useState(null);

  useEffect(() => {
    fetchTrainingHub();
  }, []);

  const fetchTrainingHub = async () => {
    try {
      const res = await api.get('/training/hub');
      setHubData(res.data);
    } catch (err) {
      toast.error('Failed to load training hub');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="training-hub-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={() => navigate('/talent/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <BookOpen className="w-8 h-8" />
                Training Hub
              </h1>
              <p className="text-white/80 mt-2">
                Complete training modules to earn certifications and unlock premium gigs
              </p>
            </div>
            {hubData?.stats && (
              <div className="hidden md:flex items-center gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold">{hubData.stats.total_certifications}</div>
                  <div className="text-sm text-white/70">Certifications</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{hubData.stats.completion_rate}%</div>
                  <div className="text-sm text-white/70">Completed</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* In Progress Section */}
        {hubData?.in_progress?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-purple-500" />
              Continue Learning
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hubData.in_progress.map(item => (
                <Card 
                  key={item.module_id} 
                  className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-purple-500"
                  onClick={() => navigate(`/talent/training/module/${item.module_id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{item.module_name}</h3>
                        <p className="text-sm text-gray-500">{item.category_name}</p>
                      </div>
                      <Badge variant="outline" className="bg-purple-50">
                        {item.progress_percent}%
                      </Badge>
                    </div>
                    <Progress value={item.progress_percent} className="mt-3 h-2" />
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                      <span>{item.lessons_completed}/{item.total_lessons} lessons</span>
                      <span>{item.estimated_minutes} min</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Certifications Earned */}
        {hubData?.certifications?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Your Certifications
            </h2>
            <div className="flex flex-wrap gap-3">
              {hubData.certifications.map(cert => (
                <Badge 
                  key={cert.id} 
                  className="bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border-amber-300 px-4 py-2"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {cert.badge_name}
                  {cert.is_verified && <Shield className="w-3 h-3 ml-2 text-green-600" />}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Training Categories */}
        <h2 className="text-lg font-semibold mb-4">Training Categories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {hubData?.categories?.map(category => {
            const IconComponent = categoryIcons[category.icon] || BookOpen;
            const isComplete = category.completed_count === category.module_count && category.module_count > 0;
            
            return (
              <Card 
                key={category.id}
                className="cursor-pointer hover:shadow-lg transition-all group"
                onClick={() => navigate(`/talent/training/category/${category.slug}`)}
                data-testid={`category-${category.slug}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${
                      isComplete 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-purple-100 text-purple-600 group-hover:bg-purple-200'
                    } transition-colors`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-lg">{category.name}</h3>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-500 transition-colors" />
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-600">
                            {category.module_count} module{category.module_count !== 1 ? 's' : ''}
                          </span>
                          <span className="text-green-600 font-medium">
                            {category.completed_count} completed
                          </span>
                        </div>
                        {category.module_count > 0 && (
                          <div className="w-24">
                            <Progress value={category.progress_percent} className="h-2" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Why Get Certified */}
        <Card className="mt-8 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-purple-500" />
              Why Get Certified?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Award className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium">Stand Out</p>
                  <p className="text-sm text-gray-600">Certified talent appears higher in search results</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Lock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Unlock Premium Gigs</p>
                  <p className="text-sm text-gray-600">Some high-paying gigs require certifications</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium">Build Trust</p>
                  <p className="text-sm text-gray-600">Verified badges show clients you're trained</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrainingHub;
